import { useEffect, useRef, useState } from 'react';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/config/featureFlags';
import { isProductionRuntime } from '@/config/runtime';
import { debugLogger } from '@/utils/debug';
import './adunit.css';

// Manual AdUnit component for controlled ad placement
interface AdUnitProps {
  adSlot: string;
  adFormat: 'BANNER' | 'RECTANGLE' | 'LEADERBOARD' | 'SKYSCRAPER' | 'NATIVE';
  className?: string;
  showLabel?: boolean;
}

const AdUnit = ({ adSlot, adFormat, className = '', showLabel = true }: AdUnitProps) => {
  const adContainerRef = useRef<HTMLDivElement>(null);
  const adElementRef = useRef<HTMLModElement>(null);
  const hasInitialized = useRef(false);
  const isAdsEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_ADS);
  const [adState, setAdState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const isProd = isProductionRuntime();

  useEffect(() => {
    if (!isAdsEnabled || !isProd || hasInitialized.current) return;

    const initializeAd = () => {
      if (!adElementRef.current || !adContainerRef.current) return;

      try {
        debugLogger.log(`[AdUnit] Initializing ad - Slot: ${adSlot}, Format: ${adFormat}`);
        
        // Mark as initialized to prevent duplicate initialization
        hasInitialized.current = true;
        
        // Push the ad to AdSense for rendering
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        
        // Set up monitoring for ad loading
        let checkCount = 0;
        const maxChecks = 30; // 15 seconds total (500ms * 30)
        
        const checkAdLoaded = () => {
          checkCount++;
          
          if (!adContainerRef.current) {
            setAdState('error');
            return;
          }
          
          // Check for iframe indicating successful ad load
          const iframes = adContainerRef.current.querySelectorAll('iframe');
          const adContent = adContainerRef.current.querySelector('.adsbygoogle');
          
          if (iframes.length > 0 && adContent) {
            setAdState('loaded');
            debugLogger.log(`[AdUnit] Ad loaded successfully - Slot: ${adSlot}`);
            return;
          }
          
          // Continue checking up to max attempts
          if (checkCount < maxChecks) {
            setTimeout(checkAdLoaded, 500);
          } else {
            setAdState('error');
            console.warn(`[AdUnit] Ad failed to load after ${maxChecks * 500}ms - Slot: ${adSlot}`);
          }
        };
        
        // Start monitoring after a brief delay to allow AdSense to start processing
        setTimeout(checkAdLoaded, 1000);
        
      } catch (error) {
        hasInitialized.current = false; // Reset on error to allow retry
        setAdState('error');
        console.error(`[AdUnit] Error initializing ad - Slot: ${adSlot}:`, error);
      }
    };

    // Wait for AdSense script to be available
    const waitForAdSense = () => {
      if (typeof window.adsbygoogle !== 'undefined') {
        setTimeout(initializeAd, 100);
      } else {
        setTimeout(waitForAdSense, 100);
      }
    };

    waitForAdSense();
  }, [adSlot, adFormat, isAdsEnabled, isProd]);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      hasInitialized.current = false;
    };
  }, []);

  if (!isAdsEnabled) return null;

  if (!isProd) {
    return (
      <div className={`w-full mx-auto ${className}`}>
        <div
          data-testid="ad-placeholder"
          className="relative min-h-[90px] w-full overflow-hidden rounded-md border border-dashed border-brand-cloud bg-brand-mist"
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-xs text-brand-slate sm:text-sm">
            <span>Advertisement</span>
            <span className="text-xs mt-1">Slot: {adSlot} | Format: {adFormat}</span>
          </div>
        </div>
        {showLabel && <div className="mt-1 text-center text-xs text-brand-slate">Advertisement</div>}
      </div>
    );
  }

  if (adState === 'error') {
    return (
      <div className={`w-full mx-auto ${className}`} aria-hidden="true">
        <div
          data-testid="ad-error"
          className="relative min-h-[90px] w-full overflow-hidden rounded-md bg-brand-mist/70"
        >
          <div className="absolute inset-0 bg-[linear-gradient(110deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.55)_50%,rgba(255,255,255,0)_100%)] opacity-60 animate-pulse" />
        </div>
      </div>
    );
  }

  const shouldShowLabel = showLabel && adState === 'loaded';

  return (
    <div className={`w-full mx-auto ${className}`}>
      <div
        ref={adContainerRef}
        className="relative min-h-[90px] w-full overflow-hidden rounded-md"
      >
        {adState === 'loading' && (
          <div className="absolute inset-0 overflow-hidden rounded-md bg-brand-mist" aria-hidden="true">
            <div className="absolute inset-0 bg-[linear-gradient(110deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.7)_45%,rgba(255,255,255,0)_100%)] animate-pulse" />
          </div>
        )}
        <ins
          ref={adElementRef}
          className="adsbygoogle block min-h-[90px]"
          data-ad-client="ca-pub-3120381243696579"
          data-ad-slot={adSlot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
      {shouldShowLabel && <div className="mt-1 text-center text-xs text-brand-slate">Advertisement</div>}
    </div>
  );
};

export default AdUnit;
